import React, { type PropsWithChildren } from 'react';

import { IntlProvider, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import english_messages from '../../../i18n/en';
import { fetchLanguageForPackages } from '../../utils/i18n-util';

const useI18n = (locale: string, source: string): Record<string, string> => {
	const [messages, setMessages] = React.useState<Record<string, string>>(english_messages);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	React.useEffect(() => {
		let aborted = false;
		setMessages(english_messages);
		fetchLanguageForPackages(locale)
			.then((messages) => {
				if (!aborted) {
					setMessages(messages);
				}
			})
			.catch((error) => {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'error',
						actionSubject: 'i18n_messages_load_error',
						source,
						attributes: {
							locale,
							error: error.message,
						},
					},
				}).fire();
			});

		return () => {
			aborted = true;
		};
	}, [locale, source, createAnalyticsEvent]);

	return messages;
};

const MessagesIntlProvider = (
	props: PropsWithChildren<{
		experience: string;
	}>,
): React.JSX.Element => {
	const { children } = props;
	const intl = useIntl();
	const messages = useI18n(intl.locale, props.experience);

	return (
		<IntlProvider {...intl} messages={messages}>
			{children}
		</IntlProvider>
	);
};

export default MessagesIntlProvider;
